import ID from 'react-form-builder2/lib/UUID';

export interface LoaderTestimonial {
  id: string;
  name: string;
  text: string;
  imageUrl?: string;
}

export interface LoaderStage {
  id: string;
  label: string;
}

/** An animated "analysing your answers" screen shown before a result. */
export interface LoadingAnalysisConfig {
  title: string;
  subtitle: string;
  stages: LoaderStage[];
  /** Seconds each stage's bar takes to fill. */
  secondsPerStage: number;
  testimonials: LoaderTestimonial[];
  /** Move on to the next step once every stage is complete. */
  autoAdvance: boolean;
}

export interface SummaryDetail {
  id: string;
  icon: string;
  label: string;
  text: string;
}

/** A personalised result card: image, level meter, insight box and key facts. */
export interface ResultSummaryConfig {
  title: string;
  imageUrl: string;
  meterTitle: string;
  meterBadge: string;
  lowLabel: string;
  midLabel: string;
  highLabel: string;
  /** Marker position along the meter, 0–100. */
  meterPosition: number;
  insightTitle: string;
  insightText: string;
  details: SummaryDetail[];
}

/** A rising curve from "now" to a goal date a number of weeks away. */
export interface ProgressChartConfig {
  title: string;
  subtitle: string;
  startLabel: string;
  goalLabel: string;
  weeks: number;
  showGoalDate: boolean;
  footnote: string;
}

const newId = (): string => ID.uuid();

export const createDefaultLoaderConfig = (): LoadingAnalysisConfig => ({
  title: 'Creating your personalised plan',
  subtitle: '<b>600,000+ people</b> have joined us',
  stages: [
    { id: newId(), label: 'Analysing your answers' },
    { id: newId(), label: 'Assessing your profile' },
    { id: newId(), label: 'Selecting the right exercises' },
    { id: newId(), label: 'Creating your personalised plan' },
  ],
  secondsPerStage: 2,
  testimonials: [
    {
      id: newId(),
      name: 'Caroline M.',
      text: 'My child was happy and laughing, which means the world to me.',
    },
  ],
  autoAdvance: true,
});

export const createDefaultSummaryConfig = (): ResultSummaryConfig => ({
  title: 'Your Summary Report',
  imageUrl: '',
  meterTitle: 'Current level',
  meterBadge: 'Developing',
  lowLabel: 'Low',
  midLabel: 'Developing',
  highLabel: 'Optimal',
  meterPosition: 55,
  insightTitle: 'What this means',
  insightText: 'Now is the perfect window to build on these strengths.',
  details: [
    { id: newId(), icon: '🎯', label: 'Main challenge', text: '{{main_challenge}}' },
    { id: newId(), icon: '💡', label: 'Best approach', text: 'Short daily exercises' },
  ],
});

export const createDefaultChartConfig = (): ProgressChartConfig => ({
  title: 'The last plan you will ever need',
  subtitle: 'Based on your answers, we expect you to reach your goal by',
  startLabel: 'Now',
  goalLabel: 'Goal',
  weeks: 4,
  showGoalDate: true,
  footnote: '',
});

/* Saved blocks may predate a setting; fill gaps from the defaults. */
export const resolveLoaderConfig = (
  value?: Partial<LoadingAnalysisConfig>,
): LoadingAnalysisConfig => ({ ...createDefaultLoaderConfig(), ...(value || {}) });

export const resolveSummaryConfig = (
  value?: Partial<ResultSummaryConfig>,
): ResultSummaryConfig => ({ ...createDefaultSummaryConfig(), ...(value || {}) });

export const resolveChartConfig = (
  value?: Partial<ProgressChartConfig>,
): ProgressChartConfig => ({ ...createDefaultChartConfig(), ...(value || {}) });

export const QUIZ_BLOCK_DEFAULTS: Record<string, () => object> = {
  LoadingAnalysis: createDefaultLoaderConfig,
  ResultSummary: createDefaultSummaryConfig,
  ProgressChart: createDefaultChartConfig,
};

export const QUIZ_BLOCK_KEYS = Object.keys(QUIZ_BLOCK_DEFAULTS);

export { newId as newQuizItemId };
